import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { ToastService } from '../../services/toast/toast.service';
import { ViewAsService } from '../../services/view-as/view-as.service';
import { ContextService } from '../../services/context/context.service';
import { RequestMapper } from '../../request-mapper';
import { LoaderComponent } from '../../atoms/loader/loader.component';

// pick a role (and an establishment for establishment roles), preview the view /
// permission tree it grants, then optionally VIEW THE APP AS that role: the menu,
// route guard, in-screen gating and the API's authorization all follow the role's
// template until Exit (sidebar banner). This is the role template, not any one
// user's materialised permissions.
@Component({
  selector: 'app-sa-view-as-role',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './sa-view-as-role.component.html',
  styleUrls: ['./sa-view-as-role.component.scss', '../../../common-style/dropdown.scss'],
})
export class SaViewAsRoleComponent implements OnInit {
  roleId = new FormControl<number | null>(null);
  establishmentId = new FormControl<number | null>(null);
  roles: any[] = [];
  establishments: any[] = [];
  role: any = null; // meta of the previewed role (from the view-as endpoint)
  tree: any[] = [];
  hasSelected: boolean = false;
  isLoading: boolean = false;

  constructor(
    private superAdminService: SuperAdminService,
    private toastService: ToastService,
    private viewAsService: ViewAsService,
    private contextService: ContextService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.superAdminService.listRoles().subscribe({
      next: (res: any) => (this.roles = (res?.data?.roles || []).filter((r: any) => Number(r.status) !== 0)),
      error: (err) => this.toastService.error(err?.error?.message || 'Failed to load roles.'),
    });
    this.superAdminService.listEstablishments().subscribe({
      next: (res: any) => (this.establishments = res?.data?.establishments || []),
      error: () => {},
    });
  }

  // establishment roles act in one branch — ask for it
  get needsEstablishment(): boolean {
    return !!this.role && this.role.applicableFor !== 'CENTRAL';
  }

  onRoleChange(): void {
    const roleId = this.roleId.value;
    if (!roleId) return;
    this.isLoading = true;
    this.hasSelected = true;
    this.superAdminService.viewAsRole(roleId).subscribe({
      next: (res: any) => {
        this.role = res?.data?.role || this.roles.find((r) => r.roleId === roleId) || null;
        this.tree = res?.data?.permissionsV2 || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.role = null;
        this.tree = [];
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load preview.');
      },
    });
  }

  enterViewAs(): void {
    if (!this.role) return;
    if (!this.tree.length) {
      this.toastService.error('This role grants no views — nothing to show.');
      return;
    }
    const establishment = this.establishments.find(
      (e) => Number(e.establishmentId) === Number(this.establishmentId.value)
    );
    if (this.needsEstablishment && !establishment) {
      this.toastService.error('Select the establishment to view this role in.');
      return;
    }
    this.viewAsService.enter(
      {
        roleId: Number(this.role.roleId),
        roleKey: this.role.roleKey,
        roleName: this.role.roleName,
        applicableFor: this.role.applicableFor,
        establishmentId: establishment ? Number(establishment.establishmentId) : null,
        establishmentName: establishment?.name ?? null,
      },
      this.tree
    );
    const route = this.contextService.firstRoute();
    this.router
      .navigateByUrl(route ? `/${RequestMapper.SIDENAV}/${route}` : `/${RequestMapper.SIDENAV}`)
      .then(() => window.location.reload());
  }
}
